<template>
    <Card pageTitle="Loyalty Program">
        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_LOYALTY">
            <div class="settings-wrap">
                <div class="page-head">
                    <h2>Loyalty Settings</h2>
                    <p>
                        Configure visit-based earning, expiry, and full-refund clawback. Points are never
                        calculated from cannabis purchase value.
                    </p>
                </div>

                <div class="status-panel">
                    <div>
                        <span :class="['status-pill', enabled ? 'on' : 'off']">{{ enabled ? "Enabled" : "Disabled" }}</span>
                        <h3>Visit-based loyalty</h3>
                        <p>
                            {{ enabled ? "POS can earn and redeem points." : "Program remains hidden from POS while disabled." }}
                        </p>
                    </div>
                    <label class="switch-row" for="loyalty-enabled">
                        <input id="loyalty-enabled" type="checkbox" v-model="enabled" :disabled="!canEdit || saving" />
                        <span>Enable</span>
                    </label>
                </div>

                <div class="settings-grid">
                    <div class="field-row">
                        <div class="field-label">Points per qualifying visit</div>
                        <CustomInput type="number" :step="1" v-model="pointsPerVisit" :disabled="!canEdit || saving" />
                    </div>

                    <div class="field-row">
                        <div class="field-label">Cooldown between earning visits</div>
                        <CustomInput type="number" :step="1" v-model="cooldownHours" :disabled="!canEdit || saving" />
                        <small class="hint">Hours. Use 0 for every paid visit.</small>
                    </div>

                    <div class="field-row">
                        <div class="field-label">Points expiry</div>
                        <CustomInput type="number" :step="1" v-model="pointsExpiryDays" :disabled="!canEdit || saving" />
                        <small class="hint">Days. Use 0 for no expiry.</small>
                    </div>
                </div>

                <div class="field-row checkbox-row">
                    <input id="loyalty-clawback" type="checkbox" v-model="clawbackOnFullRefund" :disabled="!canEdit || saving" />
                    <label for="loyalty-clawback">Claw back earned points when an invoice is fully refunded</label>
                </div>

                <div class="actions">
                    <Button borderOnly class="me-2" @click="loadConfig" :disabled="loading || saving">Reload</Button>
                    <Button v-if="canEdit" @click="saveConfig" :disabled="loading || saving">
                        {{ saving ? "Saving..." : "Save" }}
                    </Button>
                </div>
            </div>
        </AllowPermission>
    </Card>
</template>

<script>
import { mapState } from "vuex"
import { checkHasPermission, PERMISSION_CODES } from "../../store/permission"
import Card from "../../components/UI/Card.vue"
import Button from "../../components/UI/Button.vue"
import CustomInput from "../../components/crud/custom-input.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import LoyaltyFetchApi from "../../utils/fetch/loyalty-fetch-api"
import { errMsg } from "../../utils/common"

export default {
    components: { Card, Button, CustomInput, AllowPermission },
    data() {
        return {
            PERMISSION_CODES,
            loading: false,
            saving: false,
            enabled: false,
            pointsPerVisit: 10,
            cooldownHours: 24,
            pointsExpiryDays: 0,
            clawbackOnFullRefund: true,
        }
    },
    computed: {
        ...mapState("auth", ["user"]),
        canEdit() {
            return checkHasPermission({
                isAdmin: this.user?.isAdmin,
                permissionId: PERMISSION_CODES.CMS_EDIT_LOYALTY,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
    },
    async mounted() {
        await this.loadConfig()
    },
    methods: {
        async loadConfig() {
            this.loading = true
            try {
                const config = await LoyaltyFetchApi.fetchSettings()
                this.enabled = !!config?.enabled
                this.pointsPerVisit = Number(config?.pointsPerVisit ?? 10)
                this.cooldownHours = Number(config?.cooldownHours ?? 24)
                this.pointsExpiryDays = Number(config?.pointsExpiryDays ?? 0)
                this.clawbackOnFullRefund = !!config?.clawbackOnFullRefund
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not load loyalty settings"))
            }
            this.loading = false
        },
        async saveConfig() {
            if (!this.canEdit || this.saving) return
            const pointsPerVisit = Number(this.pointsPerVisit)
            const cooldownHours = Number(this.cooldownHours)
            const pointsExpiryDays = Number(this.pointsExpiryDays)
            if (!Number.isFinite(pointsPerVisit) || pointsPerVisit < 0) {
                this.$toast.error("Points per visit must be a non-negative number")
                return
            }
            if (!Number.isFinite(cooldownHours) || cooldownHours < 0) {
                this.$toast.error("Cooldown hours must be a non-negative number")
                return
            }
            if (!Number.isFinite(pointsExpiryDays) || pointsExpiryDays < 0) {
                this.$toast.error("Points expiry days must be a non-negative number")
                return
            }
            this.saving = true
            try {
                const updated = await LoyaltyFetchApi.updateSettings({
                    enabled: this.enabled,
                    pointsPerVisit,
                    cooldownHours,
                    pointsExpiryDays,
                    clawbackOnFullRefund: this.clawbackOnFullRefund,
                })
                this.enabled = !!updated?.enabled
                this.pointsPerVisit = Number(updated?.pointsPerVisit ?? pointsPerVisit)
                this.cooldownHours = Number(updated?.cooldownHours ?? cooldownHours)
                this.pointsExpiryDays = Number(updated?.pointsExpiryDays ?? pointsExpiryDays)
                this.clawbackOnFullRefund = !!updated?.clawbackOnFullRefund
                this.$toast.success("Loyalty settings updated")
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not update loyalty settings"))
            }
            this.saving = false
        },
    },
}
</script>

<style lang="scss" scoped>
.settings-wrap { max-width: 900px; }
.page-head { margin-bottom: 20px; }
.page-head h2 { margin: 0 0 6px; font-size: 24px; font-weight: 700; color: #0b302c; }
.page-head p { margin: 0; color: #5f6368; line-height: 1.45; }
.status-panel { display: flex; justify-content: space-between; gap: 20px; align-items: center; border: 1px solid #d9e4e1; border-radius: 8px; background: #f7fbfa; padding: 16px; margin-bottom: 18px; }
.status-panel h3 { margin: 8px 0 4px; font-size: 18px; font-weight: 700; color: #0b302c; }
.status-panel p { margin: 0; color: #5f6368; }
.status-pill { display: inline-flex; border-radius: 999px; padding: 4px 10px; font-size: 12px; font-weight: 700; }
.status-pill.on { background: #e8f5ec; color: #1f7a3b; }
.status-pill.off { background: #eef0f2; color: #4b5563; }
.settings-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
.field-row { margin-bottom: 16px; }
.field-label { font-weight: 600; margin-bottom: 6px; }
.checkbox-row { display: flex; align-items: center; gap: 8px; }
.switch-row { display: inline-flex; align-items: center; gap: 8px; min-width: 96px; justify-content: center; border: 1px solid #0b302c; border-radius: 8px; padding: 10px 12px; cursor: pointer; color: #0b302c; font-weight: 700; }
.switch-row input { width: 16px; height: 16px; }
.hint { display: block; color: #80868b; margin-top: 4px; }
.actions { display: flex; align-items: center; }
@media (max-width: 900px) {
    .settings-grid { grid-template-columns: 1fr; }
    .status-panel { align-items: flex-start; flex-direction: column; }
}
</style>
